import React, { useState, useEffect } from "react";
import {
  Button,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FiSearch } from "react-icons/fi";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PhoneIcon from "@mui/icons-material/Phone";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import useToast from "../../hooks/useToast";
import Toast from "../../components/Toast";

// Tab definitions
const conversationTypes = [
  { label: "All", value: "all", color: "#6B7280", activeColor: "#1F2937", activeBg: "#F3F4F6" },
  { label: "Call", value: "call", icon: PhoneIcon, color: "#7B61FF", activeColor: "#4338CA", activeBg: "#E0E7FF" },
  { label: "GMeet", value: "event", icon: VideocamIcon, color: "#4285F4", activeColor: "#1D4ED8", activeBg: "#DBEAFE" },
];

const statusOptions = [
  { label: "All", value: "all" },
  { label: "In-Progress", value: "in-progress" },
  { label: "Processing", value: "processing" },
  { label: "Completed", value: "completed" },
];

function getStatusChip(status) {
  const map = {
    "in-progress": { label: "In-Progress", bg: "#DBEAFE", color: "#1E40AF" },
    "processing":  { label: "Processing",  bg: "#EDE9FE", color: "#6366F1" },
    "pending":     { label: "Pending",     bg: "#FEF3C7", color: "#D97706" },
    "completed":   { label: "Completed",   bg: "#D1FAE5", color: "#059669" },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <Chip
      label={s.label}
      size="small"
      style={{ background: s.bg, color: s.color, fontWeight: 600, fontSize: "0.72rem", borderRadius: 8, height: 22, letterSpacing: "0.01em" }}
    />
  );
}

function getCallTypeChip(type) {
  if (type === "call") {
    return (
      <Chip
        label="Call"
        size="small"
        icon={<PhoneIcon style={{ fontSize: 12, color: "#4338CA", marginLeft: 6 }} />}
        style={{ background: "#EEF2FF", color: "#4338CA", fontWeight: 600, fontSize: "0.72rem", borderRadius: 8, height: 22 }}
      />
    );
  }
  return (
    <Chip
      label="Meeting"
      size="small"
      icon={<VideocamIcon style={{ fontSize: 14, color: "#1D4ED8", marginLeft: 4 }} />}
      style={{ background: "#DBEAFE", color: "#1D4ED8", fontWeight: 600, fontSize: "0.72rem", borderRadius: 8, height: 22 }}
    />
  );
}

export default function Meeting() {
  const [statusFilter, setStatusFilter] = useState("all");
  const [conversationType, setConversationType] = useState("all");
  const [search, setSearch] = useState("");
  const [date, setDate] = useState("All");
  const [customDateStart, setCustomDateStart] = useState("");
  const [customDateEnd, setCustomDateEnd] = useState("");
  const [page, setPage] = useState(1);
  const [meetings, setMeetings] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const { toast, success, error, hideToast, info } = useToast();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const limit = 10;

  const isMeetingInDateRange = (meeting, dateFilter) => {
    if (dateFilter === "All") return true;
    if (dateFilter === "custom") {
      if (!customDateStart || !customDateEnd) return true;
      const d = new Date(meeting.startTs);
      const s = new Date(customDateStart);
      const e = new Date(customDateEnd);
      e.setHours(23, 59, 59, 999);
      return d >= s && d <= e;
    }
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const last7Days = new Date(today); last7Days.setDate(last7Days.getDate() - 7);
    const d = new Date(meeting.startTs);
    const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    switch (dateFilter) {
      case "Today": return dOnly.getTime() === today.getTime();
      case "Yesterday": return dOnly.getTime() === yesterday.getTime();
      case "Last 7 Days": return dOnly >= last7Days && dOnly <= today;
      default: return true;
    }
  };

  const filteredMeetings = meetings.filter((m) => {
    const title = m.title?.toLowerCase() || "";
    const statusMatch = statusFilter === "all" || m.status === statusFilter;
    const searchMatch = title.includes(search.toLowerCase());
    const dateMatch = isMeetingInDateRange(m, date);
    return statusMatch && searchMatch && dateMatch;
  });

  useEffect(() => { setPage(1); }, [search, statusFilter, conversationType, date]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const params = deleteTarget.type === "call"
        ? { callSid: deleteTarget.callSid }
        : { meetingId: deleteTarget.meetingId };
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/event/delete-event`,
        null,
        { headers: { Authorization: `Bearer ${token}` }, params }
      );
      success(deleteTarget.type === "call" ? "Call deleted" : "Meeting deleted");
      fetchMeeting();
    } catch (err) {
      error("Failed to delete");
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const fetchMeeting = async () => {
    try {
      const params = { page, limit };
      if (conversationType === "call") params.type = "call";
      else if (conversationType === "event") params.type = "event";

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/calendar/get-user-event`,
        { headers: { Authorization: `Bearer ${token}` }, params }
      );

      const now = new Date();
      const mappedMeetings = response.data.meetings.map((m) => {
        const startDate = new Date(m.startTime);
        const endDate = new Date(m.endTime);
        const isCall = m.type === "call";

        let status = "in-progress";
        if (isCall) {
          if (m.llmStatus === "true") status = "completed";
          else if (m.llmStatus === "failed") status = "processing";
          else status = "in-progress";
        } else {
          if (m.isDeleted) status = "cancelled";
          else if (now < startDate) status = "pending";
          else if (m.meetingStatus === "llm_completed") status = "completed";
          else if (m.meetingStatus === null) status = "in-progress";
        }

        return {
          title: m.summary || "No Title",
          date: new Date(m.startTime).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }),
          time: new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }).format(startDate),
          status,
          rawType: m.type,
          meetingId: m.meetingId || "",
          callSid: m.callSid || "",
          rawId: m._id || "",          // raw DB _id — used for call navigation
          googleEventId: m.googleEventId || "",
          startTs: startDate.getTime(),
          endTs: endDate.getTime(),
        };
      });

      const sorted = mappedMeetings.slice().sort((a, b) => {
        const order = { "in-progress": 0, "pending": 1, "completed": 2 };
        const catA = order[a.status] ?? 3;
        const catB = order[b.status] ?? 3;
        if (catA !== catB) return catA - catB;
        return b.startTs - a.startTs;
      });

      setMeetings(sorted);
      const total = response.data.totalCount || response.data.meetings.length;
      setTotalPages(Math.ceil(total / limit) || 1);
    } catch (err) {
      console.log("Error in fetchMeeting", err?.message);
    }
  };

  useEffect(() => { fetchMeeting(); }, [page, conversationType]);

  const handleRowClick = (m) => {
    if (m.status === "pending") { info("Meeting hasn't started yet."); return; }
    if (m.rawType === "call") {
      // For calls, navigate using callSid or raw _id
      const callId = m.callSid || m.rawId;
      if (callId) navigate(`/home/meeting/${callId}`);
      return;
    }
    if (m.status === "in-progress") { info("Meeting is in progress"); return; }
    if (m.meetingId) navigate(`/home/meeting/${m.meetingId}`);
  };

  const colStyle = (flex, extra = {}) => ({
    flex,
    fontSize: "0.8125rem",
    color: "#374151",
    ...extra,
  });

  const headerCol = (flex, label, extra = {}) => (
    <div style={{ flex, fontSize: "0.7rem", fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", ...extra }}>
      {label}
    </div>
  );

  return (
    <div style={{ background: "#FFFFFF", borderRadius: 16, paddingBottom: 16 }}>
      <Toast open={toast.open} message={toast.message} severity={toast.severity} title={toast.title} duration={toast.duration} onClose={hideToast} position={toast.position} />

      <div style={{ fontSize: 26, fontWeight: 700, color: "#111827", marginBottom: 20 }}>Meetings</div>

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search meetings..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><FiSearch style={{ color: "#9CA3AF" }} /></InputAdornment>,
            style: { borderRadius: 8, background: "#F9FAFB", fontSize: "0.875rem" },
          }}
          sx={{ minWidth: 240, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" } }}
        />
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          renderValue={(v) => `Status: ${statusOptions.find(o => o.value === v)?.label || "All"}`}
          sx={{ minWidth: 140, background: "#F9FAFB", borderRadius: 2, fontSize: "0.875rem", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" } }}
        >
          {statusOptions.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
        </Select>

        {/* Type filter tabs */}
        <div style={{ display: "flex", gap: 6, background: "#F3F4F6", borderRadius: 10, padding: "3px" }}>
          {conversationTypes.map((opt) => {
            const Icon = opt.icon;
            const active = conversationType === opt.value;
            return (
              <Tooltip key={opt.value} title={opt.label} arrow>
                <button
                  onClick={() => setConversationType(opt.value)}
                  style={{
                    display: "flex", alignItems: "center", gap: 5,
                    padding: "5px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                    background: active ? "#FFFFFF" : "transparent",
                    color: active ? opt.activeColor : "#6B7280",
                    fontWeight: active ? 700 : 500,
                    fontSize: "0.8125rem",
                    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    transition: "all 0.15s ease",
                  }}
                >
                  {Icon && <Icon style={{ fontSize: 16 }} />}
                  {opt.label}
                </button>
              </Tooltip>
            );
          })}
        </div>

        <div style={{ flex: 1 }} />
        <Select
          value={date}
          onChange={(e) => { setDate(e.target.value); if (e.target.value !== "custom") { setCustomDateStart(""); setCustomDateEnd(""); } }}
          size="small"
          renderValue={(v) => (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <CalendarTodayIcon style={{ fontSize: 15, color: "#6B7280" }} />
              <span style={{ fontSize: "0.875rem" }}>{v === "custom" ? "Custom Range" : v}</span>
            </div>
          )}
          sx={{ minWidth: 140, background: "#F9FAFB", borderRadius: 2, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" } }}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Today">Today</MenuItem>
          <MenuItem value="Yesterday">Yesterday</MenuItem>
          <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
          <MenuItem value="custom">Custom Range</MenuItem>
        </Select>
        {date === "custom" && (
          <>
            <input
              type="date"
              value={customDateStart}
              onChange={(e) => setCustomDateStart(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: "0.875rem", background: "#F9FAFB", color: "#374151" }}
            />
            <span style={{ color: "#9CA3AF", fontSize: "0.875rem" }}>→</span>
            <input
              type="date"
              value={customDateEnd}
              min={customDateStart || undefined}
              onChange={(e) => setCustomDateEnd(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: "0.875rem", background: "#F9FAFB", color: "#374151" }}
            />
          </>
        )}
      </div>

      {/* Table */}
      <div style={{ borderRadius: 12, border: "1px solid #E5E7EB", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", padding: "10px 20px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", gap: 16 }}>
          {headerCol(3, "Title")}
          {headerCol(1.2, "Date")}
          {headerCol(0.8, "Time")}
          {headerCol(0.8, "Type")}
          {headerCol(0.8, "Status")}
          {headerCol(1.5, "Actions", { textAlign: "center" })}
        </div>

        {filteredMeetings.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "#9CA3AF", fontSize: "0.9rem" }}>
            No records found.
          </div>
        ) : (
          filteredMeetings.map((m, i) => (
            <div
              key={i}
              style={{
                display: "flex", alignItems: "center", padding: "13px 20px", gap: 16,
                borderBottom: i === filteredMeetings.length - 1 ? "none" : "1px solid #F3F4F6",
                background: "#FFFFFF", cursor: "pointer", transition: "background 0.15s ease",
              }}
              onClick={() => handleRowClick(m)}
              onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#FFFFFF"}
            >
              {/* Title — summary only */}
              <div style={{ flex: 3, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "#111827", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.title}
                </div>
              </div>

              {/* Date */}
              <div style={{ ...colStyle(1.2), display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                <CalendarTodayIcon style={{ fontSize: 14, color: "#9CA3AF", flexShrink: 0 }} />
                {m.date}
              </div>

              {/* Time */}
              <div style={{ ...colStyle(0.8), whiteSpace: "nowrap" }}>{m.time}</div>

              {/* Type */}
              <div style={{ flex: 0.8 }}>{getCallTypeChip(m.rawType)}</div>

              {/* Status */}
              <div style={{ flex: 0.8 }}>{getStatusChip(m.status)}</div>

              {/* Actions */}
              <div style={{ flex: 1.5, display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => handleRowClick(m)}
                  style={{ color: "#1A73E8", fontWeight: 600, fontSize: "0.8rem", textTransform: "none", padding: "4px 10px", borderRadius: 6, whiteSpace: "nowrap" }}
                >
                  View Notes
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (m.status === "in-progress") { info("Can't delete a record in progress"); return; }
                    setDeleteTarget({ type: m.rawType, callSid: m.callSid, meetingId: m.meetingId });
                    setDeleteConfirmOpen(true);
                  }}
                  style={{ fontWeight: 600, borderRadius: 6, fontSize: "0.8rem", padding: "4px 10px", borderColor: "#FCA5A5", color: "#DC2626", background: "#FEF2F2", textTransform: "none", whiteSpace: "nowrap", marginLeft: 8 }}
                  onMouseOver={(e) => { e.currentTarget.style.background = "#FEE2E2"; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = "#FEF2F2"; }}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 16, gap: 12 }}>
        <span style={{ color: "#6B7280", fontSize: "0.875rem" }}>Page {page} of {totalPages}</span>
        <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" shape="rounded" size="small" />
      </div>

      {/* Delete Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} PaperProps={{ style: { borderRadius: 12, maxWidth: 400, width: "100%" } }}>
        <DialogTitle style={{ fontSize: "1.1rem", fontWeight: 700, color: "#111827" }}>
          Delete this {deleteTarget?.type === "call" ? "call" : "meeting"}?
        </DialogTitle>
        <DialogContent style={{ color: "#6B7280", fontSize: "0.875rem" }}>
          This action cannot be undone.
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px" }}>
          <Button onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }} style={{ borderRadius: 8, textTransform: "none", color: "#6B7280", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDelete} style={{ borderRadius: 8, textTransform: "none", background: "#DC2626", fontWeight: 600 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
